import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  GraduationCap,
  Layers,
  Link2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  createAdminAcademyModule,
  deleteAdminAcademyModule,
  fetchAdminAcademy,
  fetchAdminAcademyModule,
  patchAdminAcademyModule,
  type AdminAcademyModuleRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const TYPE_ICONS: Record<string, typeof BookOpen> = {
  mock_tests: Layers,
  practice: Layers,
  previous_papers: BookOpen,
  syllabus: BookOpen,
  study_material: BookOpen,
  custom: Link2,
};

function CreateModuleModal({
  open,
  onClose,
  opportunities,
  types,
}: {
  open: boolean;
  onClose: () => void;
  opportunities: Array<{ id: string; title: string }>;
  types: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    opportunityId: opportunities[0]?.id ?? '',
    label: '',
    url: '',
    type: types[0] ?? 'custom',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      opportunityId: opportunities[0]?.id ?? '',
      label: '',
      url: '',
      type: types.includes('custom') ? 'custom' : (types[0] ?? 'custom'),
    });
    setError('');
  }, [open, opportunities, types]);

  const create = useMutation({
    mutationFn: () =>
      createAdminAcademyModule({
        opportunityId: form.opportunityId,
        label: form.label.trim(),
        url: form.url.trim(),
        type: form.type,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-academy'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create prep module');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-academy-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Prep content</p>
            <h2 id="create-academy-title"><GraduationCap size={20} /> New prep module</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Opportunity</span>
            <select
              required
              value={form.opportunityId}
              onChange={(e) => setForm((f) => ({ ...f, opportunityId: e.target.value }))}
            >
              {opportunities.map((opp) => (
                <option key={opp.id} value={opp.id}>{opp.title}</option>
              ))}
            </select>
          </label>
          <label className="wfx-admin-field">
            <span>Label</span>
            <input required value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>URL</span>
            <input
              required
              placeholder="https://academy.webfoxx.com/..."
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            />
          </label>
          <label className="wfx-admin-field">
            <span>Type</span>
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              {types.map((type) => (
                <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add module'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModuleDetailCard({
  moduleId,
  types,
  onClose,
}: {
  moduleId: string | null;
  types: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-academy-module', moduleId],
    queryFn: () => fetchAdminAcademyModule(moduleId!),
    enabled: Boolean(moduleId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<Pick<AdminAcademyModuleRow, 'label' | 'url' | 'type'>>) =>
      patchAdminAcademyModule(moduleId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-academy'] });
      void qc.invalidateQueries({ queryKey: ['admin-academy-module', moduleId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteAdminAcademyModule(moduleId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-academy'] });
      onClose();
    },
  });

  const module = data?.module;
  const Icon = TYPE_ICONS[module?.type ?? 'custom'] ?? BookOpen;

  return (
    <AdminDetailCard
      open={Boolean(moduleId)}
      onClose={onClose}
      ariaLabel="Prep module details"
      breadcrumb="Academy module"
      title={module?.label ?? 'Loading…'}
      illustration="feature.mock-test"
      subtitle={
        module ? (
          <>
            {module.isCustom ? (
              <AdminStatusBadge status="enabled" />
            ) : (
              <span className="chip-pill">Default template</span>
            )}
            <span className="wfx-muted wfx-admin-subline">{module.opportunityTitle}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading prep module…" />}
      {isError && <ErrorState message="Could not load prep module." onRetry={() => void refetch()} />}

      {module && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Type</dt><dd>{module.typeLabel}</dd></div>
            <div><dt>Source</dt><dd>{module.isCustom ? 'Custom set' : 'Default template'}</dd></div>
            <div>
              <dt>Opportunity</dt>
              <dd>
                <Link to={`/opportunities/${module.opportunitySlug}`} className="secondary-action compact" target="_blank">
                  {module.opportunityTitle}
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
              </dd>
            </div>
            <div>
              <dt>External link</dt>
              <dd>
                <a href={module.url} className="secondary-action compact" target="_blank" rel="noreferrer">
                  Open URL
                  <ExternalLink size={12} aria-hidden="true" />
                </a>
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-academy-preview glass-surface">
            <Icon size={20} aria-hidden="true" />
            <div>
              <strong>{module.label}</strong>
              <span className="wfx-muted">{module.url}</span>
            </div>
          </div>

          <div className="wfx-admin-drawer-section">
            <h3>Edit module</h3>
            <div className="wfx-admin-form">
              <label className="wfx-admin-field">
                <span>Label</span>
                <input
                  defaultValue={module.label}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && value !== module.label) update.mutate({ label: value });
                  }}
                />
              </label>
              <label className="wfx-admin-field">
                <span>URL</span>
                <input
                  defaultValue={module.url}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && value !== module.url) update.mutate({ url: value });
                  }}
                />
              </label>
              <label className="wfx-admin-field">
                <span>Type</span>
                <select
                  defaultValue={module.type}
                  onChange={(e) => update.mutate({ type: e.target.value })}
                >
                  {types.map((type) => (
                    <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="secondary-action compact wfx-admin-danger-action"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm('Remove this prep module from the opportunity?')) remove.mutate();
                }}
              >
                <Trash2 size={16} aria-hidden="true" />
                Remove module
              </button>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function ModuleCard({
  module,
  onSelect,
}: {
  module: AdminAcademyModuleRow;
  onSelect: () => void;
}) {
  const Icon = TYPE_ICONS[module.type] ?? BookOpen;

  return (
    <button type="button" className="wfx-admin-academy-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head">
        <span className="wfx-admin-academy-type">
          <Icon size={16} aria-hidden="true" /> {module.typeLabel}
        </span>
        {!module.isCustom && <span className="chip-pill">Default</span>}
      </div>
      <strong>{module.label}</strong>
      <span className="wfx-muted">{module.opportunityTitle}</span>
      <span className="wfx-muted wfx-admin-academy-url">{module.url}</span>
    </button>
  );
}

export function AdminAcademyScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [opportunityFilter, setOpportunityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, typeFilter, opportunityFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-academy', debouncedQuery, typeFilter, opportunityFilter, page],
    queryFn: () =>
      fetchAdminAcademy({
        q: debouncedQuery || undefined,
        type: typeFilter,
        opportunityId: opportunityFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const opportunities = data?.opportunities ?? [];
  const types = data?.types ?? ['custom'];
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Prep modules', value: stats?.modules ?? 0 },
      { label: 'Opportunities', value: stats?.opportunities ?? 0 },
      { label: 'Custom sets', value: stats?.customSets ?? 0 },
      { label: 'Link types', value: stats?.types ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading academy…" />;
  if (isError) return <ErrorState message="Could not load academy content." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Academy"
        title="Academy"
        description="Manage prep modules linked to opportunities — mock tests, papers, syllabus, and study material."
        icon={GraduationCap}
        illustration="feature.mock-test"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New module
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((module) => (
            <ModuleCard key={module.id} module={module} onSelect={() => setSelectedId(module.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No prep modules match your filters." />}

        <AdminTablePanel
          title="All prep modules"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search label, opportunity, or URL…"
              />
              <select
                className="wfx-admin-select"
                value={opportunityFilter}
                onChange={(e) => setOpportunityFilter(e.target.value)}
                aria-label="Filter by opportunity"
              >
                <option value="all">All opportunities</option>
                {opportunities.map((opp) => (
                  <option key={opp.id} value={opp.id}>{opp.title}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by module type"
              >
                <option value="all">All types</option>
                {types.map((type) => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} modules
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Module</th><th>Opportunity</th><th>Type</th><th>Source</th><th>URL</th></tr>
            </thead>
            <tbody>
              {rows.map((module) => (
                <tr
                  key={module.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(module.id)}
                >
                  <td><strong>{module.label}</strong></td>
                  <td>{module.opportunityTitle}</td>
                  <td>{module.typeLabel}</td>
                  <td>{module.isCustom ? 'Custom' : 'Default'}</td>
                  <td className="wfx-admin-academy-url-cell">{module.url}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateModuleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        opportunities={opportunities}
        types={types}
      />
      <ModuleDetailCard
        moduleId={selectedId}
        types={types}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
